import React, { useState, useEffect } from 'react';
import { Modal, Switch, notification } from 'antd';
import { IonIcon } from '@ionic/react';
import { 
  settingsOutline, 
  lockClosedOutline, 
  globeOutline,
  shieldCheckmarkOutline,
  informationCircleOutline
} from 'ionicons/icons';
import { useAuth } from '../../contexts/AuthContext';
import './SettingsModal.css';

const SettingsModal = ({ isOpen, onClose }) => {
  const { userPreferences, updatePreferences, preferencesLoading } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [localPreferences, setLocalPreferences] = useState({
    defaultPrivacy: 'ask', // 'ask', 'private', 'public'
  });

  // Sync local state with user preferences when modal opens
  useEffect(() => {
    if (isOpen && userPreferences) {
      setLocalPreferences({
        defaultPrivacy: userPreferences.defaultPrivacy || 'ask',
      });
    }
  }, [isOpen, userPreferences]);

  const handlePrivacyChange = (value) => {
    setLocalPreferences(prev => ({
      ...prev,
      defaultPrivacy: value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const success = await updatePreferences(localPreferences);
      if (success) {
        notification.success({
          message: 'Settings saved',
          description: 'Your preferences have been updated.',
          placement: 'top',
          duration: 3,
        });
        onClose();
      } else {
        notification.error({
          message: 'Error',
          description: 'Failed to save settings. Please try again.',
          placement: 'top',
          duration: 3,
        });
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to save settings. Please try again.',
        placement: 'top',
        duration: 3,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const privacyOptions = [
    {
      value: 'ask',
      icon: settingsOutline,
      title: 'Ask me each time',
      description: 'Show the sharing prompt after each design',
      color: '#667eea',
    },
    {
      value: 'private',
      icon: lockClosedOutline,
      title: 'Always keep private',
      description: 'All new designs will be private by default',
      color: '#6b7280',
    },
    {
      value: 'public',
      icon: globeOutline,
      title: 'Always share publicly',
      description: 'All new designs will be shared to the community',
      color: '#10b981',
    },
  ];

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={480}
      className="settings-modal"
      centered
      destroyOnClose
    >
      <div className="settings-modal-content">
        {/* Header */}
        <div className="settings-modal-header">
          <div className="settings-modal-icon">
            <IonIcon icon={shieldCheckmarkOutline} />
          </div>
          <div className="settings-modal-title">
            <h2>Privacy Settings</h2>
            <p>Control how your designs are shared</p>
          </div>
        </div>

        {/* Privacy Section */}
        <div className="settings-section">
          <div className="settings-section-header">
            <IonIcon icon={shieldCheckmarkOutline} className="section-icon" />
            <div>
              <h3>Default Privacy</h3>
              <p>Choose what happens when you finish a design</p>
            </div>
          </div>

          <div className="privacy-options">
            {privacyOptions.map((option) => (
              <div
                key={option.value}
                className={`privacy-option ${localPreferences.defaultPrivacy === option.value ? 'selected' : ''}`}
                onClick={() => handlePrivacyChange(option.value)}
                style={{
                  '--option-color': option.color,
                }}
              >
                <div className="privacy-option-radio">
                  <div className="radio-outer">
                    <div className="radio-inner" />
                  </div>
                </div>
                <div className="privacy-option-icon">
                  <IonIcon icon={option.icon} />
                </div>
                <div className="privacy-option-content">
                  <h4>{option.title}</h4>
                  <p>{option.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="settings-note">
            <IonIcon icon={lockClosedOutline} />
            <span>You can always change privacy from your file list later</span>
          </div>
        </div>

        {/* Footer */}
        <div className="settings-modal-footer">
          <button 
            className="settings-btn settings-btn-cancel"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button 
            className="settings-btn settings-btn-save"
            onClick={handleSave}
            disabled={isSaving || preferencesLoading}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SettingsModal;

